import React, { useEffect, useMemo, useState, useRef } from 'react'
import { createStyles } from '@codeleap/styles'
import { theme } from '@/config'
import { resampleWaveform } from '../../audioWaveform'

interface WaveformProps {
  isRecording: boolean
  // When provided, renders the played slice (bars whose normalized index is
  // less than playbackProgress) in the primary color. Range: 0..1.
  playbackProgress?: number
  // Real amplitude samples (0..1) computed from the recorded blob. When set,
  // overrides the live animation/snapshot and renders the actual waveform.
  samples?: number[] | null
  barCount?: number
}

const BASE_HEIGHT = 4
const MAX_HEIGHT = 20
const BAR_COLOR_IDLE = '#9CA3AF'
const BAR_COLOR_PLAYED = theme.baseColors?.primarySolid500 || '#4F46E5'

export const Waveform: React.FC<WaveformProps> = ({ isRecording, playbackProgress = 0, samples, barCount = 50 }) => {
  const [bars, setBars] = useState<number[]>(Array(barCount).fill(BASE_HEIGHT))

  // When real samples are available (preview after decode), use them.
  // Otherwise fall back to the live animation / last animated frame.
  const sampleBars = useMemo(() => {
    if (!samples || samples.length === 0) return null
    const resampled = resampleWaveform(samples, barCount)
    return resampled.map((value) => {
      const normalized = Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0))
      return BASE_HEIGHT + (MAX_HEIGHT - BASE_HEIGHT) * normalized
    })
  }, [samples, barCount])
  const animationFrameRef = useRef<number>()
  const startTimeRef = useRef<number>(Date.now())
  const barOffsetsRef = useRef<number[]>(
    Array(barCount).fill(0).map(() => Math.random() * Math.PI * 2)
  )

  useEffect(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = undefined
    }

    // When recording stops we deliberately leave `bars` as it was on the last
    // frame so the preview shows the same visual the user just produced. The
    // bars only reset to flat on a fresh mount.
    if (!isRecording) return

    startTimeRef.current = Date.now()
    let lastUpdate = Date.now()

    const animate = () => {
      const now = Date.now()
      if (now - lastUpdate > 50) {
        lastUpdate = now
        const elapsed = now - startTimeRef.current

        setBars(
          barOffsetsRef.current.map((offset) => {
            const wave1 = Math.sin((elapsed / 300) + offset)
            const wave2 = Math.sin((elapsed / 150) + offset * 2)
            const wave3 = Math.sin((elapsed / 450) + offset * 0.5)
            const combined = (wave1 + wave2 * 0.5 + wave3 * 0.3) / 1.8
            const randomVariation = Math.random() * 0.3
            const normalized = (combined + randomVariation + 1) / 2
            return BASE_HEIGHT + (MAX_HEIGHT - BASE_HEIGHT) * normalized
          })
        )
      }
      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animationFrameRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = undefined
      }
    }
  }, [isRecording, barCount])

  const renderedBars = sampleBars ?? bars
  const playedBars = Math.floor(renderedBars.length * Math.min(Math.max(playbackProgress, 0), 1))

  return (
    <div style={styles.wrapper}>
      {renderedBars.map((height, i) => (
        <div
          key={i}
          style={{
            ...styles.bar,
            height: `${height}px`,
            background: i < playedBars ? BAR_COLOR_PLAYED : BAR_COLOR_IDLE,
          }}
        />
      ))}
    </div>
  )
}

const styles = createStyles(theme => ({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    flex: 1,
    maxWidth: 400,
    height: 24,
  },
  bar: {
    width: 2,
    borderRadius: 1,
    transition: 'height 0.1s ease-out, background 0.05s linear',
  },
}))
